import { create } from "zustand";
import { DatasourceDto } from "./generated/api-client";
import { getApi } from "./get-api";
import { datasourceForMember, normaliseAccess } from "./member-access";
import { useAuth } from "./useAuth";

export type Store = {
  datasources: DatasourceDto[];
  isLoading: boolean;
  /**
   * True until the first source-list request settles (success or failure).
   * Refetches keep the last list so the sidebar does not flicker.
   */
  isInitialLoad: boolean;
  /** Set when the first load fails before any list is known. */
  fetchError: string | null;
  setDatasources: (datasources: DatasourceDto[]) => void;
  fetchDatasources: () => Promise<void>;
};

let fetchRequestId = 0;

export const useDatasources = create<Store>((set, get) => {
  return {
    datasources: [],
    isLoading: false,
    isInitialLoad: true,
    fetchError: null,
    setDatasources: (datasources: DatasourceDto[]) =>
      set({
        datasources,
        fetchError: null,
        // Clearing (logout) resets so the next session waits on first load.
        ...(datasources.length === 0 ? { isInitialLoad: true } : {}),
      }),
    fetchDatasources: async () => {
      const requestId = ++fetchRequestId;
      try {
        // Keep the last list while loading so chrome does not flicker.
        set({ isLoading: true, fetchError: null });
        const token = useAuth.getState().token;
        const api = getApi({ token });
        const response = await api.datasources.getDatasources();
        if (requestId !== fetchRequestId) return;
        // GET /datasources still lists every table. The latest analysis omits
        // a hidden table, so a Custom member follows that list.
        const datasources = await Promise.all(
          response.data.map(async (row) => {
            const kind = normaliseAccess(
              (row as { access?: string | null }).access ?? row.viewerRole,
            );
            if (kind !== "CUSTOM" || !row.id) return row;
            try {
              const latest = await api.analysis.getLatestAnalyzeAttempt(row.id);
              return datasourceForMember(
                row,
                latest.data?.databaseCollections ?? [],
              );
            } catch {
              return row;
            }
          }),
        );
        if (requestId !== fetchRequestId) return;
        set({
          datasources,
          isInitialLoad: false,
          fetchError: null,
        });
      } catch (error) {
        console.error("Error fetching datasources:", error);
        if (requestId !== fetchRequestId) return;
        // Keep the last successful list on failure.
        if (get().isInitialLoad) {
          set({
            isInitialLoad: false,
            fetchError: "Could not load your data sources.",
          });
        } else {
          set({ fetchError: "Could not refresh your data sources." });
        }
      } finally {
        if (requestId === fetchRequestId) {
          set({ isLoading: false });
        }
      }
    },
  };
});
